import { useEffect, useCallback, useRef } from 'react';
import { useCompanyStore } from '../stores/useCompanyStore';
import { logger } from '@/lib/logger';

/**
 * Hook personalizado para sincronizar datos cuando cambie la empresa activa
 * @param {Function} dataFetcher - Función que obtiene los datos
 * @param {Array} dependencies - Dependencias adicionales para el useEffect
 * @param {Function} onSyncComplete - Callback opcional que se ejecuta cuando la sincronización se completa
 * @returns {Object} - Estado y funciones de control
 */
export const useCompanyDataSync = (dataFetcher, dependencies = [], onSyncComplete = null) => {
  const { activeCompany, lastCompanyChange } = useCompanyStore();
  const lastSyncRef = useRef(null);

  const syncData = useCallback(async () => {
    if (!activeCompany) {
      return;
    }

    // Evitar sincronizaciones duplicadas solo si es la misma empresa y el mismo timestamp
    const syncKey = `${activeCompany.id}-${lastCompanyChange}`;
    if (lastSyncRef.current === syncKey) {
      return;
    }

    try {
      lastSyncRef.current = syncKey;
      
      await dataFetcher();
      
      // Ejecutar callback de completado si existe
      if (onSyncComplete) {
        onSyncComplete(activeCompany);
      }
    } catch (error) {
      logger.error(`Error sincronizando datos para empresa ${activeCompany.name}:`, error);
      lastSyncRef.current = null; // Reset para permitir reintento
    }
  }, [activeCompany, dataFetcher, lastCompanyChange, onSyncComplete]);

  // Sincronizar cuando cambie la empresa o el timestamp de cambio
  useEffect(() => {
    // Solo ejecutar si hay una empresa activa
    if (!activeCompany) {
      return;
    }
    
    syncData();
  }, [activeCompany?.id, lastCompanyChange, syncData]);

  return {
    activeCompany,
    lastCompanyChange,
    syncData,
    isCompanySelected: !!activeCompany,
    companyId: activeCompany?.id
  };
};

/**
 * Hook para forzar actualización global de todos los datos
 */
export const useGlobalDataRefresh = () => {
  const { forceDataRefresh } = useCompanyStore();
  
  const refreshAllData = useCallback(() => {
    forceDataRefresh();
  }, [forceDataRefresh]);

  return { refreshAllData };
};

export default useCompanyDataSync;
